import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { usersApi } from "../services/users";
import type { DjSearchHit } from "../types/User";
import { SERVER_URL } from "../config";
import "./DjSearch.css";
import { t as tr } from "../i18n";
import { T } from "../i18n/T";
import { DEFAULT_AVATAR_SRC } from "../lib/defaultAvatar";

// Fan-side DJ search on the landing page. Founder picks:
// A2 quiet text entry under Industry Leaders, B1 the button expands into the
// field in place (fan never leaves the page), C2 stacked rows. Founder
// follow-up (after seeing it live): rows also show the profile photo —
// photo when the DJ has one, their initial otherwise, never a broken image
// (same rule as the tab-bar avatar). Matching, ranking and the 8-row cap
// live server-side (GET /users/search); this component just debounces and
// renders.

const absUrl = (u?: string | null) =>
  !u ? "" : u.startsWith("http") ? u : `${SERVER_URL}${u}`;

// Photo when it loads, the drawn default picture otherwise (1.5.2). Broken/
// missing images drop to it instead of the browser's broken-image glyph.
const RowAvatar: React.FC<{ avatarUrl: string | null }> = ({ avatarUrl }) => {
  const [broken, setBroken] = useState(false);
  const src = absUrl(avatarUrl);
  return (
    <img
      className="djsearch-avatar"
      src={!src || broken ? DEFAULT_AVATAR_SRC : src}
      alt=""
      loading="lazy"
      onError={() => setBroken(true)}
    />
  );
};

const DjSearch: React.FC<{
  /** App fan home (locked r6 §5): the field renders open from the start —
   *  no collapsed entry line, and no auto-focus (the keyboard would cover
   *  the one-screen page; fans tap the field when they mean it). */
  alwaysOpen?: boolean;
  /** Landing page (founder decision): the collapsed "Search DJs →" line is
   *  the fan door, so it goes to the fan app (Browse tab, with the three fan
   *  tabs) instead of unfolding a search box on the marketing page. */
  entryTo?: string;
  /** Who opens a result (the talent's Discover, 1.5.3): the caller navigates
   *  instead of this component. Without it a row opens the talent's page. */
  onOpen?: (slug: string) => void;
}> = ({ alwaysOpen, entryTo, onOpen }) => {
  const [open, setOpen] = useState(!!alwaysOpen);
  const [q, setQ] = useState("");
  // null = nothing to show under the field (idle, short query, or a failed
  // request — a network error must never render as "No DJs found").
  const [hits, setHits] = useState<Hit[] | null>(null);
  const [searching, setSearching] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  type Hit = DjSearchHit;

const seqRef = useRef(0);
  const navigate = useNavigate();

  useEffect(() => {
    if (open && !alwaysOpen) inputRef.current?.focus();
  }, [open, alwaysOpen]);

  const cleaned = q.trim().replace(/^@/, "");

  useEffect(() => {
    if (!open) return;
    if (cleaned.length < 2) {
      setHits(null);
      setSearching(false);
      return;
    }
    const seq = ++seqRef.current;
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const data = await usersApi.search(cleaned);
        if (seq !== seqRef.current) return;
        setHits(Array.isArray(data) ? data : null);
      } catch {
        if (seq === seqRef.current) setHits(null);
      } finally {
        if (seq === seqRef.current) setSearching(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [open, cleaned]);

  if (!open) {
    return (
      <button
        type="button"
        className="djsearch-entry"
        onClick={() => (entryTo ? navigate(entryTo) : setOpen(true))}
      >
        <T k="djSearch.lookingForSomeoneSearch" tags={{ 0: (c) => <b>{c}</b> }} />
      </button>
    );
  }

  return (
    <div className="djsearch">
      <div className="djsearch-field">
        <span className="djsearch-mag" aria-hidden>🔍</span>
        <input
          ref={inputRef}
          className="djsearch-input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={tr("djSearch.nameInstagramOrVenue")}
          autoComplete="off"
          autoCapitalize="off"
          spellCheck={false}
          aria-label={tr("djSearch.searchDjs")}
        />
        {q && (
          <button
            type="button"
            className="djsearch-clear"
            aria-label={tr("connectBank.clearSearch")}
            onClick={() => {
              setQ("");
              inputRef.current?.focus();
            }}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" aria-hidden="true">
              <path d="M7 7l10 10M17 7L7 17" />
            </svg>
          </button>
        )}
      </div>

      {cleaned.length < 2 ? (
        // Fan home renders the field permanently (alwaysOpen) — a standing
        // helper line under an untouched field is noise; it appears once
        // they start typing.
        q.length > 0 || !alwaysOpen ? (
          <p className="djsearch-hint">{tr("djSearch.typeAtLeastLetters")}</p>
        ) : null
      ) : hits === null ? null : hits.length === 0 && !searching ? (
        <div className="djsearch-empty">
          <div className="djsearch-empty-big">{tr("djSearch.noDjsFoundFor", { q: q.trim() })}</div>
          <div className="djsearch-empty-small">
            {tr("djSearch.checkTheSpellingOr")}
          </div>
        </div>
      ) : (
        <div className="djsearch-rows">
          {hits.map((h) => (
            <button
              key={h.slug}
              type="button"
              className="djsearch-row"
              // fromBrowse: the DJ page shows a back arrow only for fans who
              // came from here — a QR arrival has nowhere to go back to.
              onClick={() => (onOpen ? onOpen(h.slug) : navigate(`/${h.slug}`, { state: { fromBrowse: true } }))}
            >
              <RowAvatar avatarUrl={h.avatarUrl} />
              <span className="djsearch-stack">
                <span className="djsearch-name">{h.name}</span>
                {/* Found by their venue: that's what the line says, so the
                    result makes sense to whoever typed it. */}
                {h.venueName && h.venueName.toLowerCase().includes(cleaned.toLowerCase()) ? (
                  <span className="djsearch-ig djsearch-venue">
                    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                      <path d="M12 2a7 7 0 0 0-7 7c0 5.2 7 13 7 13s7-7.8 7-13a7 7 0 0 0-7-7Zm0 9.5a2.5 2.5 0 1 1 0-5 2.5 2.5 0 0 1 0 5Z" />
                    </svg>
                    <span className="djsearch-venue-name">{h.venueName}</span>
                  </span>
                ) : (
                  h.instagram && <span className="djsearch-ig">@{h.instagram}</span>
                )}
              </span>
              <span className="djsearch-chev" aria-hidden>›</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default DjSearch;
